import { Stack } from '@mantine/core';
import { Appointment, Patient } from '@medplum/fhirtypes';
import { PaymentOrderState } from '@/hooks/usePaymentOrder';
import { RefundHistoryEntry } from '@/services/Timeline/refundCards';
import { FEATURE_FLAGS } from '@bariendo/lib/constants';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useFeatureFlagEnabled } from '@/hooks/useFeatureFlag';
import { useTimelineContext } from '../hooks/useTimelineContext';
import { CareTeamCard } from './CareTeam/CareTeamCard';
import { ContactCard } from './Contact/ContactCard';
import { RelatedContactsCard } from './RelatedContact/RelatedContactsCard';
import { IntakeBillingSection } from './IntakeBilling/IntakeBillingSection';
import { InsuranceSection } from './Insurance/InsuranceSection';
import { useInsuranceCard } from './Insurance/useInsuranceCard';
import { Section } from './shared/Section';
import { PatientProcedures } from './PatientProcedures';
import { AboutSection } from './AboutSection';
import { BMISection } from './BMISection';
import { AllergiesList } from './AllergiesList';
import { MedicationsList } from './MedicationsList';
import { LabsList } from './LabsList';
import { PatientSummarySkeleton } from '@/components/TimelineSkeleton';

export function PatientSummary() {
  const { patientContext, appointmentsContext, paymentOrderContext, refundRequestsContext } = useTimelineContext();
  const patient = patientContext?.patient;
  // Procedures sourced from the lifted appointmentsContext on TimelineContext —
  // PatientTimelineHeader's offering pill reads from the same slice, avoiding
  // duplicate Appointment queries per page load.
  const procedureAppointments = appointmentsContext?.procedures ?? [];
  const procedureAppointmentsLoading = appointmentsContext?.loading ?? false;
  const { currentUserPermissions } = useCurrentUser();
  const showIntakeBilling = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_INTAKE_BILLING_PANEL);

  // Patient Access works the relationship, not the chart: no clinical panels (BAR-2585).
  const showClinical = currentUserPermissions.canViewClinicalRecord;
  const showLabs =
    showClinical &&
    (currentUserPermissions.canAccessRestrictedContent ||
      currentUserPermissions.isAdmin ||
      currentUserPermissions.hasAdminMembership);

  if (!patient) {
    return <PatientSummarySkeleton />;
  }

  return (
    <Stack gap={8}>
      <Section title="About" defaultOpen>
        <AboutSection patient={patient} />
      </Section>
      <Section title="Treatment" defaultOpen>
        <PatientProcedures appointments={procedureAppointments} loading={procedureAppointmentsLoading} />
      </Section>
      <Section title="Body measurements" defaultOpen>
        <BMISection patient={patient} />
      </Section>
      <ContactCard patient={patient} />
      <RelatedContactsCard />
      <IntakeAndInsurance
        patient={patient}
        showIntakeBilling={showIntakeBilling}
        appointments={appointmentsContext?.all ?? []}
        procedures={procedureAppointments}
        paymentOrder={paymentOrderContext}
        refundRequests={refundRequestsContext?.requests}
        onPatientChanged={patientContext.refreshPatient}
      />
      <CareTeamCard />
      {showClinical && (
        <Section title="Allergies" defaultOpen={false}>
          <AllergiesList />
        </Section>
      )}
      {showClinical && (
        <Section title="Medications" defaultOpen={false}>
          <MedicationsList />
        </Section>
      )}
      {showLabs && (
        <Section title="Labs" defaultOpen={false}>
          <LabsList />
        </Section>
      )}
    </Stack>
  );
}

interface IntakeAndInsuranceProps {
  patient: Patient;
  showIntakeBilling: boolean;
  appointments: Appointment[];
  procedures: Appointment[];
  paymentOrder?: PaymentOrderState;
  refundRequests?: RefundHistoryEntry[];
  onPatientChanged: () => Promise<void>;
}

/** Intake and billing, then Insurance: both draw the insurance card, read once here. */
function IntakeAndInsurance({ patient, showIntakeBilling, ...panel }: IntakeAndInsuranceProps) {
  const insurance = useInsuranceCard(patient);

  return (
    <>
      {showIntakeBilling && <IntakeBillingSection patient={patient} insurance={insurance} {...panel} />}
      <InsuranceSection card={insurance} />
    </>
  );
}
